import { useEffect, useMemo, useState } from 'react';
import {
  FileSignature, Plus, Download, Lock, Save, GripVertical, Link2, X, Globe,
  ExternalLink, Upload,
} from 'lucide-react';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import type { DropResult } from '@hello-pangea/dnd';
import {
  affectedReferences, brokenRefs, numberChanges, refLabel, renumber, resolveRefs,
  toBlocks, toFlat, type Block, type NumberChange,
} from './contractSections';
import toast from 'react-hot-toast';
import { contractsApi, downloadFile, openFile } from '../services/api';
import FamilyLoanForm from './FamilyLoanForm';
import type {
  ContractTemplate, TemplateSection, ContractTree, ContractRow,
  GenerationTargets,
} from '../types';

/** One contract line. Kept out of the page body because it renders at two
 *  indents — under a partner, and under a client beneath that partner. */
/** The executed copy has to be FIXED: a PDF, a scan or a photograph.
 *
 *  A Word file is excluded because it is editable by whoever holds it and
 *  renders differently depending on the fonts installed — the one document
 *  where that will not do is the one somebody signed. An image is fine: what
 *  was signed is what everyone sees, and a phone photo of the signature page is
 *  how a great many of these come back. */
const SIGNED_ACCEPT = '.pdf,.png,.jpg,.jpeg,.tif,.tiff,.heic,.webp';

function ContractRows({
  rows, indent, onChanged,
}: { rows: ContractRow[]; indent: number; onChanged?: () => void }) {
  const [busy, setBusy] = useState('');

  const upload = async (contractId: string, file?: File | null) => {
    if (!file) return;
    setBusy(contractId);
    try {
      await contractsApi.uploadDocument(contractId, 'signed', file);
      toast.success('Signed copy stored');
      onChanged?.();
    } catch (e: any) {
      // The server's own message says WHY — wrong type, contents not matching
      // the extension, share unavailable. Replacing it with "upload failed"
      // throws away the only useful part.
      toast.error(e?.message || 'Could not store the signed copy');
    } finally {
      setBusy('');
    }
  };

  if (!rows.length) return null;
  return (
    <div className={indent ? 'pl-12' : 'pl-3'}>
      {rows.map((r) => (
        <div
          key={r.id}
          className="py-1.5 pr-3 flex items-center justify-between border-t border-gray-100 dark:border-gray-800 text-sm"
        >
          <span className="text-gray-700 dark:text-gray-300">
            {r.key ? <span className="text-gray-400 mr-1.5">{r.key}</span> : null}
            {r.title}
          </span>
          <span className="flex items-center gap-2">
            {(r.documents || []).map((slot) => (
              <span key={slot} className="inline-flex rounded border border-gray-300 dark:border-gray-600 overflow-hidden">
                <button
                  onClick={() => void downloadFile(
                    `/contracts/${r.id}/documents/${slot}`, `${r.key || slot}`)}
                  className="text-[11px] px-1.5 py-0.5 flex items-center gap-1 hover:bg-gray-50 dark:hover:bg-gray-800"
                  title={`Download the ${slot} document`}
                >
                  <Download size={11} /> {slot}
                </button>
                {/* Viewing matters most for the countersigned copy — it arrives
                    as a PDF or a photo of the signature page, and downloading a
                    file to check a signature is a poor way to check it. The
                    server refuses inline for anything it will not render
                    safely, so this degrades to a download rather than breaking. */}
                <button
                  onClick={() => void openFile(`/contracts/${r.id}/documents/${slot}?disposition=inline`)}
                  className="text-[11px] px-1.5 py-0.5 border-l border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
                  title={`Open the ${slot} document in a new tab`}
                >
                  <ExternalLink size={11} />
                </button>
              </span>
            ))}
            {!(r.documents || []).length && (
              // Said plainly rather than left blank. A contract row with no
              // document and no explanation reads as a broken page.
              <span
                className="text-[11px] text-rose-600 dark:text-rose-400"
                title={r.documentError || undefined}
              >
                {r.documentError ? 'no document — hover for why' : 'no document'}
              </span>
            )}
            {/* Upload the countersigned copy. Always available — a corrected
                copy replaces an earlier one — but labelled for the state it
                matters most in. */}
            <label
              className={`text-[11px] px-1.5 py-0.5 rounded border flex items-center gap-1 cursor-pointer ${
                r.signatureMethod === 'placeholder' || !(r.documents || []).includes('signed')
                  ? 'border-accent text-accent hover:bg-accent/5'
                  : 'border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
              title={`Upload the countersigned contract. Accepts ${SIGNED_ACCEPT.replace(/\./g, '').toUpperCase()}, including a scan or a photo of the signature page.`}
            >
              <Upload size={11} />
              {busy === r.id
                ? 'Storing…'
                : (r.documents || []).includes('signed') ? 'Replace signed' : 'Upload signed'}
              <input
                type="file"
                accept={SIGNED_ACCEPT}
                className="hidden"
                disabled={busy === r.id}
                onChange={(e) => {
                  void upload(r.id, e.target.files?.[0]);
                  // Cleared so choosing the same file twice fires again — a
                  // re-upload after a failure is the common second attempt.
                  e.target.value = '';
                }}
              />
            </label>

            {/* HOW it was signed. A wet signature is a complete execution and
                reads as one — the only value that flags anything is the
                placeholder, which genuinely is not signed yet. */}
            {r.signatureMethod === 'manual' && (
              <span
                className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                title="Signed by hand and uploaded — a scan or photograph of the signature page."
              >
                manual signature
              </span>
            )}
            {r.signatureMethod === 'esignature' && (
              <span
                className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                title="Captured by an e-signature platform."
              >
                e-signature
              </span>
            )}
            {r.signatureMethod === 'placeholder' && (
              <span
                className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                title="Nothing has been signed yet — the slot holds a stand-in so the lifecycle can be exercised. Upload the countersigned copy to replace it."
              >
                awaiting signature
              </span>
            )}
            {r.templateVersion && (
              <span className="text-[11px] text-gray-400" title="The template version this was generated from">
                v{r.templateVersion}
              </span>
            )}
            <span className={`text-[11px] px-1.5 py-0.5 rounded ${
              r.status === 'executed'
                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                : r.status === 'void'
                ? 'bg-gray-100 text-gray-500 dark:bg-gray-800'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
            }`}>
              {r.status}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * One place for contracts, two tabs.
 *
 * Templates and signed agreements live together because contract capability
 * spread across Organisations and Quotes means three permission gates to keep
 * in step, and the one that gets forgotten is the one that leaks. Generation
 * still starts in context — a partner organisation, a quote — and lands here.
 */
type Tab = 'templates' | 'contracts';

export default function ContractsPage() {
  const [tab, setTab] = useState<Tab>('templates');
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [selected, setSelected] = useState<ContractTemplate | null>(null);
  const [sections, setSections] = useState<TemplateSection[]>([]);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tree, setTree] = useState<ContractTree | null>(null);
  const [targets, setTargets] = useState<GenerationTargets | null>(null);
  const [generating, setGenerating] = useState('');
  const [raisingLoan, setRaisingLoan] = useState(false);
  /** Clause numbers that moved in the last reorder. Surfaced because
   *  renumbering does not rewrite cross-references inside clause bodies. */
  const [moved, setMoved] = useState<NumberChange[]>([]);
  /** An open reference lookup: which body, and where the caret was. */
  const [lookup, setLookup] = useState<{ sectionId: string; at: number } | null>(null);
  const [lookupQuery, setLookupQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { items } = await contractsApi.listTemplates();
      setTemplates(items);
    } catch {
      toast.error('Could not load contract templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  useEffect(() => {
    if (tab !== 'contracts') return;
    if (!tree) {
      contractsApi.byOrganisation().then(setTree).catch(() =>
        toast.error('Could not load contracts by organisation'));
    }
    // Re-fetched on every entry to the tab, NOT cached alongside the tree.
    // Whether a template is published is decided in the Templates tab, so
    // caching this on the tree's lifetime leaves the Generate action disabled —
    // telling you to publish something you just published — until a full reload.
    contractsApi.generationTargets().then(setTargets).catch(() => setTargets(null));
  }, [tab, tree]);

  const open = (t: ContractTemplate) => {
    setSelected(t);
    setSections([...(t.sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0)));
    setDirty(false);
  };

  /** A published version is not editable. Its successor starts as a copy, so
   *  the published text stays exactly as it was signed. */
  const frozen = selected?.status !== 'draft';

  const grouped = useMemo(() => {
    const by: Record<string, ContractTemplate[]> = {};
    for (const t of templates) (by[t.templateId] ||= []).push(t);
    return by;
  }, [templates]);

  /** Returns false if the save failed, so callers can stop. */
  const save = async (announce = true): Promise<boolean> => {
    if (!selected) return true;
    try {
      await contractsApi.updateTemplate(selected._id, { sections });
      if (announce) toast.success('Draft saved');
      setDirty(false);
      void load();
      return true;
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not save');
      return false;
    }
  };

  const publish = async () => {
    if (!selected) return;
    // The choice is a legal judgement, so the prompt says what it means rather
    // than offering a default someone in a hurry would accept.
    const answer = window.prompt(
      'Publish this version.\n\n' +
      'minor — wording, clarity, typos. Obligations UNCHANGED.\n' +
      'major — liability, indemnity, term, governing law, or anything that ' +
      'changes what a party is agreeing to.\n\n' +
      'Type minor or major:');
    if (!answer) return;
    const bump = answer.trim().toLowerCase();
    if (bump !== 'minor' && bump !== 'major') {
      toast.error('Type exactly "minor" or "major"');
      return;
    }
    try {
      const updated = await contractsApi.publishTemplate(selected._id, bump);
      toast.success(`Published ${updated.version}`);
      void load();
      setSelected(updated);
      // Publishing is what unblocks generation on the other tab. Refreshed here
      // too so the action is live the moment you switch, rather than depending
      // on a re-mount.
      contractsApi.generationTargets().then(setTargets).catch(() => {});
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not publish');
    }
  };

  const newDraft = async () => {
    if (!selected) return;
    try {
      const draft = await contractsApi.newTemplateDraft(selected._id);
      toast.success('Draft created from this version');
      await load();
      open(draft);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Could not create a draft');
    }
  };

  /** Fetched with the token rather than linked: a plain anchor navigates
   *  without the Authorization header and returns a 401 rendered as JSON. */
  const downloadBlank = async () => {
    if (!selected) return;
    // The server renders the SAVED template, so an edit still sitting in browser
    // state would silently not appear — which reads as the editor being broken
    // rather than as unsaved work. Commit it first.
    if (dirty && !frozen) {
      toast.loading('Saving your edits first…', { id: 'blank', duration: 1200 });
      if (!(await save(false))) return;
    }
    try {
      await downloadFile(
        `/contracts/templates/${selected._id}/blank.docx`,
        `${selected.templateId}-${selected.version || 'draft'}-blank.docx`);
    } catch (e: any) {
      toast.error(e?.message || 'Could not generate the blank document');
    }
  };

  /** The published version as JSON, for the public terms page.
   *
   *  Only offered on a published version: the master agreement incorporates
   *  these terms by reference to a version number, and a draft's number is
   *  still being decided.
   */
  const downloadWebJson = async () => {
    if (!selected) return;
    try {
      await downloadFile(
        `/contracts/templates/${selected._id}/web.json`,
        `${selected.templateId}-v${selected.version}.json`);
    } catch (e: any) {
      toast.error(e?.message || 'Could not export the published terms');
    }
  };

  /** Re-read the tree after something changed a contract's documents. */
  const refreshTree = () => {
    contractsApi.byOrganisation().then(setTree).catch(() => { /* keep what we have */ });
  };

  const generateMaster = async (orgId: string, name: string) => {
    if (!window.confirm(
      `Raise a master reseller agreement with ${name}?\n\n` +
      `It will be created as a draft against published template ` +
      `v${targets?.masterTemplateVersion}. Nothing is sent to anyone.`)) return;
    setGenerating(orgId);
    try {
      const item = await contractsApi.generateMaster(orgId);
      toast.success(`Raised ${item.key || 'the agreement'}`);
      const [t, g] = await Promise.all([
        contractsApi.byOrganisation(), contractsApi.generationTargets()]);
      setTree(t);
      setTargets(g);
    } catch (e: any) {
      // The server's own message says WHY — not a partner, already has one, no
      // published version. Replacing it with a generic failure would throw away
      // the only useful part.
      toast.error(e?.message || 'Could not raise the agreement');
    } finally {
      setGenerating('');
    }
  };

  const updateSection = (id: string, patch: Partial<TemplateSection>) => {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    setDirty(true);
  };

  const blocks = useMemo(() => toBlocks(sections), [sections]);

  /** Commit a new arrangement: renumber, then report what moved. */
  const commit = (next: TemplateSection[]) => {
    const renumbered = renumber(next);
    const changes = numberChanges(sections, renumbered);
    setSections(renumbered);
    setMoved(changes);
    setDirty(true);
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination, type } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId &&
        source.index === destination.index) return;

    const next = blocks.map((b) => ({ parent: b.parent, children: [...b.children] }));

    if (type === 'CLAUSE') {
      // The whole block moves. Its subsections travel with it because they are
      // held inside it rather than inferred from position at drop time.
      const [lifted] = next.splice(source.index, 1);
      next.splice(destination.index, 0, lifted);
    } else {
      const from = next.find((b) => b.parent.id === source.droppableId);
      const to = next.find((b) => b.parent.id === destination.droppableId);
      if (!from || !to) return;
      const [lifted] = from.children.splice(source.index, 1);
      to.children.splice(destination.index, 0, lifted);
    }
    commit(toFlat(next));
  };

  /** Insert a reference at the caret the lookup was opened from.
   *
   *  The token stores the target's ID, not its number. That is the whole point:
   *  numbers move on every reorder, the clause being cited does not, and the
   *  number is resolved when the document is rendered.
   */
  const insertRef = (targetId: string) => {
    if (!lookup) return;
    const section = sections.find((x) => x.id === lookup.sectionId);
    if (!section) return;
    const body = section.body || '';
    const at = Math.min(lookup.at, body.length);
    updateSection(section.id, {
      body: `${body.slice(0, at)}{{ref:${targetId}}}${body.slice(at)}`,
    });
    setLookup(null);
    setLookupQuery('');
  };

  /** Everything a reference can point at, with how it will read. */
  const refTargets = useMemo(() => sections.map((x) => ({
    id: x.id,
    label: refLabel(x) || x.id,
    heading: x.heading || '',
    level: x.level || 1,
    excluded: !!x.excluded,
  })), [sections]);

  const broken = useMemo(() => brokenRefs(sections), [sections]);

  /** How many sections are in the rendered document right now. */
  const includedCount = sections.filter((s) => !s.excluded).length;
  const spanning = sections.filter((s) => s.spansColumns).length;

  /** Set `spansColumns` on every section at once.
   *
   *  Deliberately hits ALL sections, not just the visible ones: the control says
   *  "all", and a bulk action that silently skipped some would be worse than no
   *  bulk action. Turning them all on is a legitimate choice — it produces a
   *  single-column document.
   */
  const setAllSpans = (value: boolean) => {
    setSections((prev) => prev.map((s) => ({ ...s, spansColumns: value })));
    setDirty(true);
  };

  /** Excluding a clause excludes its subsections too.
   *
   *  Otherwise 8.1 renders under a clause 8 that is not in the document — an
   *  orphaned subsection reading as though it belongs to whatever came before.
   */
  const setBlockExcluded = (parentId: string, excluded: boolean) => {
    const block = blocks.find((b) => b.parent.id === parentId);
    const ids = new Set([parentId, ...(block?.children || []).map((c) => c.id)]);
    commit(sections.map((s) => (ids.has(s.id) ? { ...s, excluded } : s)));
  };

  const setAllExcluded = (value: boolean) => {
    setSections((prev) => prev.map((s) => ({ ...s, excluded: value })));
    setDirty(true);
  };

  /** A section id that is not already taken.
   *
   *  Ids must be unique — the API rejects duplicates, because a duplicate makes
   *  the clause diff ambiguous and lets an edit apply to the wrong clause.
   */
  const freshId = (prefix: string, taken: Set<string>) => {
    let n = 1;
    let id = `${prefix}-${n}`;
    while (taken.has(id)) id = `${prefix}-${++n}`;
    return id;
  };

  /** Insert a new clause or subsection directly after `afterId` (or at the end).
   *  Orders are renumbered so the document sequence matches what you see. */
  const addSection = (level: number, afterId?: string) => {
    setSections((prev) => {
      const taken = new Set(prev.map((s) => s.id));
      const created: TemplateSection = {
        id: freshId(level === 2 ? 'subsection' : 'clause', taken),
        heading: '',
        body: '',
        level,
        number: '',
        spansColumns: false,
        excluded: false,
        order: 0,
      };
      const at = afterId ? prev.findIndex((s) => s.id === afterId) + 1 : prev.length;
      const next = [...prev.slice(0, at), created, ...prev.slice(at)];
      return next.map((s, i) => ({ ...s, order: i }));
    });
    setDirty(true);
  };

  const matchesQuery = (t: { label: string; heading: string }) => {
    const q = lookupQuery.trim().toLowerCase();
    if (!q) return true;
    return t.label.toLowerCase().includes(q) || t.heading.toLowerCase().includes(q);
  };

  /** One section card. Clauses and subsections render identically apart from
   *  the indent — the difference between them is where they sit, not how they
   *  look, and styling them differently would imply a distinction that the
   *  data does not make. */
  const renderCard = (s: TemplateSection, handle: any, block: Block | null) => (
    <div
      className={`border rounded-lg p-3 ${
        s.excluded
          ? 'border-dashed border-gray-300 dark:border-gray-700 opacity-55'
          : 'border-gray-200 dark:border-gray-700'
      } ${block ? 'bg-white dark:bg-gray-900' : 'ml-8 bg-gray-50/60 dark:bg-gray-800/30'}`}
    >
      <div className="flex items-center justify-between mb-1.5 gap-2">
        <span
          {...(handle || {})}
          className={`shrink-0 ${frozen ? 'opacity-20' : 'cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500'}`}
          title={block
            ? 'Drag to reorder. Subsections move with the clause.'
            : 'Drag to reorder within, or into another, clause.'}
        >
          <GripVertical size={14} />
        </span>
        {s.number ? (
          <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 shrink-0">
            {s.number}
          </span>
        ) : null}
        <input
          value={s.heading || ''}
          disabled={frozen}
          onChange={(e) => updateSection(s.id, { heading: e.target.value })}
          className="text-sm font-medium bg-transparent border-0 p-0 focus:ring-0 text-gray-900 dark:text-gray-100 flex-1 min-w-0 disabled:opacity-70"
          placeholder={block ? 'Clause heading' : 'Subsection heading'}
        />
        <span className="flex items-center gap-3 shrink-0">
          <label
            className="text-[11px] text-gray-500 flex items-center gap-1.5"
            title={block
              ? 'Leaves this clause AND its subsections out of the rendered document. The text is kept.'
              : 'Leaves this subsection out of the rendered document. The text is kept.'}
          >
            <input
              type="checkbox"
              checked={!s.excluded}
              disabled={frozen}
              onChange={(e) => (block
                ? setBlockExcluded(s.id, !e.target.checked)
                : commit(sections.map((x) => (x.id === s.id
                    ? { ...x, excluded: !e.target.checked } : x))))}
            />
            include
          </label>
          <label
            className="text-[11px] text-gray-500 flex items-center gap-1.5"
            title="Breaks out of the two-column layout. Needed for the signature block and tables — a signature block split across a column break is a defect."
          >
            <input
              type="checkbox"
              checked={!!s.spansColumns}
              disabled={frozen}
              onChange={(e) => updateSection(s.id, { spansColumns: e.target.checked })}
            />
            spans columns
          </label>
        </span>
      </div>
      <div className="relative">
        <textarea
          value={s.body || ''}
          disabled={frozen}
          onChange={(e) => updateSection(s.id, { body: e.target.value })}
          onKeyDown={(e) => {
            if (e.key !== '/' || frozen) return;
            // Only at the start of a word, so `and/or` and `24/7` still type
            // normally. Hijacking every slash in a legal document would be
            // intolerable.
            const at = e.currentTarget.selectionStart;
            const before = (s.body || '').slice(at - 1, at);
            if (at !== 0 && before.trim() !== '') return;
            e.preventDefault();
            setLookup({ sectionId: s.id, at });
            setLookupQuery('');
          }}
          rows={block ? 5 : 4}
          placeholder={frozen ? '' : 'Type / to insert a reference to another clause'}
          className="w-full text-sm px-2 py-1.5 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 rounded-md disabled:opacity-70"
        />

        {lookup?.sectionId === s.id && (
          <div className="absolute z-20 left-2 right-2 top-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 shadow-lg">
            <div className="flex items-center gap-2 px-2 py-1.5 border-b border-gray-200 dark:border-gray-700">
              <Link2 size={13} className="text-accent shrink-0" />
              <input
                autoFocus
                value={lookupQuery}
                onChange={(e) => setLookupQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') { setLookup(null); setLookupQuery(''); }
                  if (e.key === 'Enter') {
                    const first = refTargets.filter(matchesQuery)[0];
                    if (first) insertRef(first.id);
                  }
                }}
                placeholder="Find a clause by number or heading…"
                className="flex-1 text-xs bg-transparent border-0 p-0 focus:ring-0"
              />
              <button
                onClick={() => { setLookup(null); setLookupQuery(''); }}
                className="text-gray-400 hover:text-gray-600 shrink-0"
              >
                <X size={13} />
              </button>
            </div>
            <div className="max-h-56 overflow-y-auto">
              {refTargets.filter(matchesQuery).slice(0, 60).map((t) => (
                <button
                  key={t.id}
                  onClick={() => insertRef(t.id)}
                  className={`w-full text-left px-2 py-1 text-xs flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800 ${
                    t.level >= 2 ? 'pl-6' : ''
                  } ${t.excluded ? 'opacity-50' : ''}`}
                >
                  <span className="font-mono text-gray-500 shrink-0">{t.label}</span>
                  <span className="truncate text-gray-700 dark:text-gray-300">{t.heading}</span>
                  {t.excluded && <span className="text-[10px] text-rose-500 shrink-0">excluded</span>}
                </button>
              ))}
              {!refTargets.filter(matchesQuery).length && (
                <p className="px-2 py-2 text-xs text-gray-400">No clause matches that.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* What the references will actually say once rendered. The stored token
          is deliberately visible in the textarea — it is the thing being
          edited — so this shows the result without hiding the mechanism. */}
      {(s.body || '').includes('{{ref:') && (
        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
          <span className="text-gray-400">renders as: </span>
          {resolveRefs(s.body || '', sections).slice(0, 220)}
        </p>
      )}
      {!frozen && (
        <div className="flex gap-3 mt-1.5 text-[11px] text-gray-400">
          <button onClick={() => addSection(1, s.id)} className="hover:text-accent underline">
            + clause below
          </button>
          <button onClick={() => addSection(2, s.id)} className="hover:text-accent underline">
            + subsection below
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-1">
        <FileSignature className="text-accent" size={22} />
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Contracts</h1>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Templates, partner master agreements and client order forms.
      </p>

      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        {(['templates', 'contracts'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm capitalize border-b-2 -mb-px ${
              tab === t
                ? 'border-accent text-accent font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'templates' && (
        <div className="grid grid-cols-12 gap-4">
          {/* Versions */}
          <div className="col-span-4 space-y-3">
            {loading && <p className="text-sm text-gray-400">Loading…</p>}
            {!loading && templates.length === 0 && (
              <div className="text-sm text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-4">
                No templates yet. The master reseller agreement and the order
                form are seeded from <code>contracts/</code> in the repo.
              </div>
            )}
            {Object.entries(grouped).map(([templateId, versions]) => (
              <div key={templateId} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {templateId}
                </div>
                {versions.map((v) => (
                  <button
                    key={v._id}
                    onClick={() => open(v)}
                    className={`w-full text-left px-3 py-2 text-sm border-t border-gray-100 dark:border-gray-800 flex items-center justify-between ${
                      selected?._id === v._id ? 'bg-accent/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                    }`}
                  >
                    <span className="text-gray-700 dark:text-gray-300">
                      {v.version || 'draft'}
                      {v.usageCount ? (
                        <span className="ml-2 text-[11px] text-gray-400">
                          used {v.usageCount}×
                        </span>
                      ) : null}
                    </span>
                    <span className={`text-[11px] px-1.5 py-0.5 rounded ${
                      v.status === 'published'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                        : v.status === 'retired'
                        ? 'bg-gray-100 text-gray-500 dark:bg-gray-800'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                    }`}>
                      {v.status}
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>

          {/* Sections */}
          <div className="col-span-8">
            {!selected && (
              <p className="text-sm text-gray-400">Select a version to view its clauses.</p>
            )}
            {selected && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {selected.title}
                    </h2>
                    <p className="text-xs text-gray-500">
                      {selected.templateId} · {selected.version || 'unpublished draft'}
                      {frozen && (
                        <span className="ml-2 inline-flex items-center gap-1 text-gray-400">
                          <Lock size={11} /> read-only
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => void downloadBlank()}
                      className="px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md flex items-center gap-1.5 hover:bg-gray-50 dark:hover:bg-gray-800"
                      title="Word document with the placeholders left in — for review, not for signature"
                    >
                      <Download size={13} /> Blank .docx
                    </button>
                    {selected.status === 'published' && (
                      <button
                        onClick={() => void downloadWebJson()}
                        className="px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md flex items-center gap-1.5 hover:bg-gray-50 dark:hover:bg-gray-800"
                        title={`Export v${selected.version} as JSON for the public terms page on www.hsinc.ai. Carries a checksum so the published page can be matched to this version.`}
                      >
                        <Globe size={13} /> Export for website
                      </button>
                    )}
                    {frozen ? (
                      <button onClick={newDraft} className="px-2.5 py-1.5 text-xs bg-accent text-white rounded-md flex items-center gap-1.5">
                        <Plus size={13} /> New draft
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => void save()}
                          disabled={!dirty}
                          className="px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md flex items-center gap-1.5 disabled:opacity-40"
                        >
                          <Save size={13} /> Save
                        </button>
                        <button onClick={publish} className="px-2.5 py-1.5 text-xs bg-accent text-white rounded-md">
                          Publish…
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center flex-wrap gap-4 mb-3 px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-800/60 text-[11px] text-gray-600 dark:text-gray-300">
                  <label className="flex items-center gap-1.5" title="Set or clear 'spans columns' on every section at once.">
                    <input
                      type="checkbox"
                      disabled={frozen}
                      checked={spanning === sections.length && sections.length > 0}
                      // Indeterminate when only some are set, so the control
                      // never misreports a mixed template as all-or-nothing.
                      ref={(el) => {
                        if (el) el.indeterminate = spanning > 0 && spanning < sections.length;
                      }}
                      onChange={(e) => setAllSpans(e.target.checked)}
                    />
                    <strong>spans columns</strong> — all
                  </label>
                  <span className="text-gray-400">{spanning} of {sections.length} span</span>

                  <span className="text-gray-300 dark:text-gray-600">|</span>

                  <button
                    disabled={frozen}
                    onClick={() => setAllExcluded(false)}
                    className="underline hover:text-accent disabled:opacity-40 disabled:no-underline"
                  >
                    include all
                  </button>
                  <button
                    disabled={frozen}
                    onClick={() => setAllExcluded(true)}
                    className="underline hover:text-accent disabled:opacity-40 disabled:no-underline"
                  >
                    exclude all
                  </button>
                  <span className="text-gray-400">
                    {includedCount} of {sections.length} in the document
                  </span>

                  <span className="text-gray-300 dark:text-gray-600">|</span>

                  <button
                    disabled={frozen}
                    onClick={() => addSection(1)}
                    className="px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded flex items-center gap-1 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-40"
                  >
                    <Plus size={11} /> Add clause at end
                  </button>
                </div>

                {!!broken.length && (
                  <div className="mb-3 px-3 py-2 rounded-md bg-rose-50 dark:bg-rose-900/20 text-[11px] text-rose-800 dark:text-rose-300">
                    <strong>{broken.length} reference{broken.length > 1 ? 's' : ''} would
                    render as a defect.</strong> A contract that cites a clause it does
                    not contain is a real problem, so these print visibly rather than
                    quietly resolving to something plausible:
                    <div className="mt-1">
                      {broken.slice(0, 8).map((b, i) => (
                        <div key={`${b.in}-${b.targetId}-${i}`}>
                          {b.in || '(untitled)'} → target {b.reason}
                        </div>
                      ))}
                      {broken.length > 8 && <div>+{broken.length - 8} more</div>}
                    </div>
                  </div>
                )}

                {!!moved.length && (
                  <div className="mb-3 px-3 py-2 rounded-md bg-amber-50 dark:bg-amber-900/20 text-[11px] text-amber-800 dark:text-amber-300">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <strong>Clause numbers changed.</strong> Cross-references
                        inside clause bodies are <strong>not</strong> rewritten —
                        &ldquo;as set out in clause 11.3&rdquo; is prose, and
                        rewriting prose automatically is how an agreement quietly
                        starts pointing at the wrong clause. Check these by hand:
                        <div className="mt-1 font-mono">
                          {moved.slice(0, 12).map((m) => (
                            <span key={`${m.from}-${m.to}`} className="mr-3 whitespace-nowrap">
                              {m.from} → {m.to}
                            </span>
                          ))}
                          {moved.length > 12 && <span>+{moved.length - 12} more</span>}
                        </div>
                        {!!affectedReferences(sections, moved).length && (
                          <div className="mt-1">
                            Mentioned in:{' '}
                            <strong>{affectedReferences(sections, moved).join(', ')}</strong>
                          </div>
                        )}
                      </div>
                      <button onClick={() => setMoved([])} className="underline shrink-0">
                        dismiss
                      </button>
                    </div>
                  </div>
                )}

                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="clauses" type="CLAUSE">
                    {(dropClause) => (
                      <div
                        ref={dropClause.innerRef}
                        {...dropClause.droppableProps}
                        className="space-y-3"
                      >
                        {blocks.map((block, bi) => (
                          <Draggable
                            key={block.parent.id}
                            draggableId={block.parent.id}
                            index={bi}
                            isDragDisabled={frozen}
                          >
                            {(dragClause, snap) => (
                              <div
                                ref={dragClause.innerRef}
                                {...dragClause.draggableProps}
                                className={snap.isDragging ? 'rounded-lg ring-2 ring-accent/40' : ''}
                              >
                                {renderCard(block.parent, dragClause.dragHandleProps, block)}

                                {/* Its own droppable, so a subsection can only
                                    land among subsections — never between two
                                    clauses, where it would silently change owner. */}
                                <Droppable droppableId={block.parent.id} type="SUB">
                                  {(dropSub) => (
                                    <div
                                      ref={dropSub.innerRef}
                                      {...dropSub.droppableProps}
                                      className="space-y-2 mt-2"
                                    >
                                      {block.children.map((child, ci) => (
                                        <Draggable
                                          key={child.id}
                                          draggableId={child.id}
                                          index={ci}
                                          isDragDisabled={frozen}
                                        >
                                          {(dragSub, subSnap) => (
                                            <div
                                              ref={dragSub.innerRef}
                                              {...dragSub.draggableProps}
                                              className={subSnap.isDragging ? 'rounded-lg ring-2 ring-accent/40' : ''}
                                            >
                                              {renderCard(child, dragSub.dragHandleProps, null)}
                                            </div>
                                          )}
                                        </Draggable>
                                      ))}
                                      {dropSub.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {dropClause.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'contracts' && (
        <div className="space-y-4 max-w-4xl">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Partners hold the master agreement; each client's order form is executed
            under it. Raise a <strong>master agreement</strong> on the partner below —
            it is signed once per partner and needs no quote or deal. An{' '}
            <strong>order form</strong> is per client and is raised from the quote it
            prices, on the quote itself.
          </p>

          {targets && !targets.masterTemplateVersion && (
            <p className="text-xs rounded-md px-3 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300">
              <strong>Generate master agreement</strong> is disabled because
              <code className="mx-1">master-reseller</code> is still a draft. Publish
              it in the <strong>Templates</strong> tab and the action becomes
              available on every partner below. Generating from a draft would record
              a version number that is still being edited onto a contract someone
              signs — and first use is what freezes that version, so the draft is the
              moment to get the wording right.
            </p>
          )}

          {tree === null && <p className="text-sm text-gray-400">Loading…</p>}

          {tree?.partners.map((p) => (
            <div key={p.id} className="border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{p.name}</span>
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                    partner
                  </span>
                  {p.environment === 'demo' && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      demo
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-gray-500">
                    {p.contracts.length ? `${p.contracts.length} agreement(s)` : 'no master agreement yet'}
                  </span>
                  {!p.contracts.some((c) => c.contractKind === 'master') && (
                    <button
                      onClick={() => void generateMaster(p.id, p.name)}
                      disabled={generating === p.id || !targets?.masterTemplateVersion}
                      className="px-2 py-1 text-[11px] border border-gray-300 dark:border-gray-600 rounded-md flex items-center gap-1 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={
                        targets?.masterTemplateVersion
                          ? `Raise the master reseller agreement with ${p.name} from published v${targets.masterTemplateVersion}. No quote or deal is involved.`
                          : 'Publish the master-reseller template first — generating from a draft would stamp a version number that is still being edited onto a signed contract.'
                      }
                    >
                      <FileSignature size={11} />
                      {generating === p.id ? 'Raising…' : 'Generate master agreement'}
                    </button>
                  )}
                </div>
              </div>

              <ContractRows rows={p.contracts} indent={0} onChanged={refreshTree} />

              {p.clients.map((c) => (
                <div key={c.id} className="border-t border-gray-100 dark:border-gray-800">
                  <div className="px-3 py-1.5 pl-8 flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">{c.name}</span>
                    <span className="text-[11px] text-gray-400">
                      {c.contracts.length ? `${c.contracts.length} order form(s)` : 'no order form yet'}
                    </span>
                  </div>
                  <ContractRows rows={c.contracts} indent={1} onChanged={refreshTree} />
                </div>
              ))}
            </div>
          ))}

          {(tree?.mayRaisePrivate || !!tree?.private?.length) && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                    <Lock size={12} /> Private agreements
                  </span>
                  <span className="text-[11px] text-gray-500">
                    Between individuals — HS Inc is not a party. Visible to super admins only.
                  </span>
                </div>
                {tree?.mayRaisePrivate && !raisingLoan && (
                  <button
                    onClick={() => setRaisingLoan(true)}
                    className="px-2 py-1 text-[11px] border border-gray-300 dark:border-gray-600 rounded-md flex items-center gap-1 hover:bg-white dark:hover:bg-gray-700"
                    title="A personal loan between two people, with its repayment schedule computed."
                  >
                    <Plus size={11} /> New family loan
                  </button>
                )}
              </div>
              {raisingLoan && (
                <FamilyLoanForm
                  onCancel={() => setRaisingLoan(false)}
                  onRaised={() => { setRaisingLoan(false); refreshTree(); }}
                />
              )}
              <ContractRows rows={tree?.private || []} indent={0} onChanged={refreshTree} />
            </div>
          )}

          {!!tree?.unmatched.length && (
            <div className="border border-rose-300 dark:border-rose-800 rounded-lg">
              <div className="px-3 py-2 bg-rose-50 dark:bg-rose-900/20">
                <p className="text-sm font-medium text-rose-800 dark:text-rose-300">
                  {tree.unmatched.length} contract{tree.unmatched.length > 1 ? 's' : ''} not
                  placed under an organisation
                </p>
                <p className="text-[11px] text-rose-700 dark:text-rose-400 mt-0.5">
                  The deal behind these resolves to no live organisation — usually
                  a CRM client pointing at one retired in a merge. Shown rather
                  than hidden: the document routes refuse a contract they cannot
                  place, so this is why it cannot be opened, not a display quirk.
                </p>
              </div>
              <ContractRows rows={tree.unmatched} indent={0} onChanged={refreshTree} />
            </div>
          )}

          {!!tree?.unassigned.length && (
            <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                No partner assigned
              </p>
              {/* Shown rather than hidden: a client with no partner cannot have
                  an order form, and that is worth seeing. */}
              {tree.unassigned.map((c) => (
                <div key={c.id} className="text-sm text-gray-600 dark:text-gray-400 py-0.5">
                  {c.name}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
